// Frequencies the in-house custom scheduler (SF-Backend #668) actually accepts. Bi-weekly / bimonthly
// are NOT supported there, so we must never tokenize a card for one — reject before vaulting.
export const SUPPORTED_RECURRING_FREQUENCIES = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'];
export const MAX_INSTALLMENTS = 52; // the custom scheduler's per-plan charge cap

export interface InstallmentPlan {
  apiFrequency: string;
  numberOfPayments: number;
  perOccurrenceAmount: number;
  error: string | null;
}

/**
 * Single source of truth for the installment plan the custom scheduler will create — used by BOTH
 * the pre-vault guard and the post-vault create call so the two can never diverge.
 *
 * Installments-first: the patient sets the PER-CHARGE installment (the amount field, floored at $10 /
 * defaulted to the 10% suggestion at the field level); the number of charges is DERIVED here so the
 * plan pays off the full balance. The custom scheduler charges this fixed amount each occurrence; the
 * backend caps the FINAL charge to the exact remaining balance (ISS-34), so N is a ceiling and the sum
 * can never over/undershoot. `billTotal` is always the full balance — never the per-charge.
 * `apiFrequency` is the already-mapped scheduler frequency (mapFrequencyToAPI is applied at the call
 * site); keeping this function free of that mapping keeps it a pure, unit-testable money calculation.
 */
export const computeInstallmentPlan = (
  billTotal: number,
  perCharge: number,
  apiFrequency: string,
): InstallmentPlan => {
  // Derive the charge count in integer CENTS — dollar division (e.g. 147.30 / 14.73) is float-unsafe
  // and could ceil to the wrong N, diverging from the cents-based UI preview.
  const perChargeCents = Math.round(perCharge * 100);
  const billTotalCents = Math.round(billTotal * 100);
  const numberOfPayments = perChargeCents > 0 ? Math.ceil(billTotalCents / perChargeCents) : 0;
  // Send the installment verbatim; the backend caps the final charge to the exact remainder.
  const perOccurrenceAmount = perCharge > 0 ? perCharge : 0;

  let error: string | null = null;
  if (!SUPPORTED_RECURRING_FREQUENCIES.includes(apiFrequency)) {
    error = "That payment frequency isn't available for scheduled payments. Please choose Weekly, Monthly, or Quarterly.";
  } else if (perOccurrenceAmount <= 0) {
    error = 'Please choose a valid amount per payment for your scheduled plan.';
  } else if (!Number.isInteger(numberOfPayments) || numberOfPayments < 1 || numberOfPayments > MAX_INSTALLMENTS) {
    error = `That amount per payment would need more than ${MAX_INSTALLMENTS} charges. Please choose a larger amount per payment.`;
  }

  return { apiFrequency, numberOfPayments, perOccurrenceAmount, error };
};
